import { BaseModel } from './base.model';
import { Deserializable } from './deserializable';
import { RoleModel } from './role.model';
import { LANGUAGES, SALUTATIONS } from '../constants';
import { TeamModel } from './team.model';
import { LanguageModel } from './language.model';
import { LocationModel } from './location.model';
import { TemplateModel } from './template.model';
import { AppointmentModel } from './appointment.model';

const ROLES_WITH_ADDITIONAL_DATA: Array<string> = ['moderator', 'observer', 'moderator_external', 'observer_gem'];

export class UserModel extends BaseModel implements Deserializable {

  // columns
  hashed_id: string; // tslint:disable-line: variable-name
  gender: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  mobile: string;
  language: string;
  company: string;
  comment: string;
  password: string;
  country: string;
  timezone: string;
  tz_offset: string; // tslint:disable-line: variable-name
  pivot: any;
  alt_emails: Array<string> | null;
  directlink?: string;

  // relations
  team: TeamModel;
  roles: Array<RoleModel>;
  languages: Array<LanguageModel>;
  locations: Array<LocationModel>;
  templates: Array<TemplateModel>;
  appointments: Array<AppointmentModel>;

  deserialize(input: any): this {
    Object.assign(this, input);
    // roles
    let roles = null;
    if (this.roles !== null && this.roles !== undefined) {
      roles = [];
      this.roles.forEach((role: RoleModel) => {
        roles.push(new RoleModel().deserialize(role));
      });
    }
    this.roles = roles;
    // languages
    let languages = null;
    if (this.languages !== null && this.languages !== undefined) {
      languages = [];
      this.languages.forEach((language: LanguageModel) => {
        languages.push(new LanguageModel().deserialize(language));
      });
    }
    this.languages = languages;
    // locations
    let locations = null;
    if (this.locations !== null && this.locations !== undefined) {
      locations = [];
      this.locations.forEach((location: LocationModel) => {
        locations.push(new LocationModel().deserialize(location));
      });
    }
    this.locations = locations;
    // templates
    let templates = null;
    if (this.templates !== null && this.templates !== undefined) {
      templates = [];
      this.templates.forEach((template: TemplateModel) => {
        templates.push(new TemplateModel().deserialize(template));
      });
    }
    this.templates = templates;
    // appointments
    let appointments = null;
    if (this.appointments !== null && this.appointments !== undefined) {
      appointments = [];
      this.appointments.forEach((appointment: AppointmentModel) => {
        appointments.push(new TemplateModel().deserialize(appointment));
      });
    }
    this.appointments = appointments;
    // team
    if (this.team !== null) {
      this.team = new TeamModel().deserialize(this.team);
    }
    return this;
  }

  public get fullname(): string {
    return this.firstname + ' ' + this.lastname;
  }

  public get fullname_reverse(): string {
    return this.lastname + ', ' + this.firstname;
  }

  public get rolenames(): Array<string> {
    const rolenames = [];
    this.roles.forEach((role: RoleModel) => {
      rolenames.push(role.rolename);
    });
    return rolenames;
  }

  public get translated_rolenames(): Array<string> {
    const rolenames = [];
    this.roles.forEach((role: RoleModel) => {
      rolenames.push((this.transloco) ? this.transloco.translate(role.rolename) : role.rolename);
    });
    return rolenames;
  }

  public get team_name(): string | null {
    return this.team?.name || null;
  }

  public get isAdmin(): boolean {
    return this.roles?.filter((role: any) => {
      return role.name === 'administrator';
    }).length > 0;
  }

  public get isModerator(): boolean {
    return this.roles?.filter((role: any) => {
      return role.name === 'moderator';
    }).length > 0;
  }

  public get isExternalModerator(): boolean {
    return this.roles?.filter((role: any) => {
      return role.name === 'moderator_external';
    }).length > 0;
  }

  public get isObserver(): boolean {
    return this.roles?.filter((role: any) => {
      return role.name === 'observer' || role.name === 'observer_gem';
    }).length > 0;
  }

  public get isForRunner(): boolean {
    return this.isModerator || this.isExternalModerator || this.isObserver;
  }

  public getLanguageName(): string {
    return (this.language) ? LANGUAGES[this.language] : '';
  }

  public getSalutation(): string {
    return (this.gender) ? SALUTATIONS[this.gender] : '';
  }

  public showAdditionalData(): boolean {
    return this.roles.filter((role: any) => {
      return ROLES_WITH_ADDITIONAL_DATA.indexOf(role.name) !== -1;
    }).length > 0;
  }

  public hasAvailabilities(): boolean {
    return this.isModerator || this.isExternalModerator || this.isObserver;
  }

}
